<script setup lang="ts">
import {computed} from "vue";
import {EWeekday, ICurrentlyOpen, IOpeningHours} from "@/api/opening-hours.model.ts";
import type {IStoreShort} from "@/api/aj-trends.model.ts";
import {useGlobalTimestamp} from "@/service/globalTimestamp.service.ts";

const props = defineProps<{ store: IStoreShort, openingHours: IOpeningHours }>()

const timestamp = useGlobalTimestamp()
const now = computed(() => new Date(timestamp.value))
const currentDay = computed(() => EWeekday.fromDate(now.value))
const currentTime = computed(() => IOpeningHours.timeOfDate(now.value))
const currentStatus = computed(() => ICurrentlyOpen.evaluate(currentDay.value, currentTime.value, props.openingHours))

const listOfWeekdays = [
  EWeekday.Mon,
  EWeekday.Tue,
  EWeekday.Wed,
  EWeekday.Thu,
  EWeekday.Fri,
  EWeekday.Sat,
  EWeekday.Sun,
] as const

const weekdayLabels: Readonly<Record<EWeekday, string>> = {
  [EWeekday.Mon]: "Montag",
  [EWeekday.Tue]: "Dienstag",
  [EWeekday.Wed]: "Mittwoch",
  [EWeekday.Thu]: "Donnerstag",
  [EWeekday.Fri]: "Freitag",
  [EWeekday.Sat]: "Samstag",
  [EWeekday.Sun]: "Sonntag",
} as const

const supportEmail = 'support@supermarkttrends.net'

const mailtoSupportEmail = computed(() => {
  const s = props.store
  const subject = `Fehler in Markt #${s.id} (${s.name} ${s.city}, ${s.street})`
  return `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}`
})
</script>

<template>
  <div class="media g-mb-20 g-pa-20--sm g-mb-minus-1">
    <div class="d-flex g-mt-7 g-mr-15">
      <span v-if="currentStatus.open" class="g-width-12 g-height-12 rounded-circle g-bg-green"></span>
      <span v-else class="g-width-12 g-height-12 rounded-circle g-bg-red"></span>
    </div>
    <div class="media-body">
      <div class="d-flex justify-content-between">
        <h5 v-if="currentStatus.open" class="g-color-green g-font-weight-700">Jetzt geöffnet</h5>
        <h5 v-else class="g-color-red g-font-weight-700">Momentan geschlossen</h5>
      </div>
      <span v-if="currentStatus.open" class="d-block">
        Noch <span class="g-font-weight-900">{{ ICurrentlyOpen.formatRemaining(currentStatus.remaining) }} </span> geöffnet
      </span>
    </div>
  </div>

  <div class="media g-mb-20 g-pa-20--sm g-mb-minus-1">
    <div class="d-flex g-mt-2 g-mr-15">
       <span><i class="fa fa-clock-o g-font-size-25"></i></span>
    </div>
    <div class="media-body">
      <div class="d-flex justify-content-between">
        <h5 class="g-font-weight-700">Öffnungszeiten</h5>
      </div>

      <div class="row">
        <template v-for="day of listOfWeekdays" :key="day">
          <div class="col-4">
            <span>{{ weekdayLabels[day] }}</span>
          </div>
          <div class="col-8 g-line-height-1_2 g-pb-10">
            <span>
              <template v-if="openingHours[day]">
                {{ IOpeningHours.formatTime(openingHours[day].openingHours[0]) }}
                &dash;
                {{ IOpeningHours.formatTime(openingHours[day].openingHours[1]) }}
                <br/>
                <template v-if="openingHours[day].lunchBreak">
                  <span class="g-font-size-12">
                  Pause
                  {{ IOpeningHours.formatTime(openingHours[day].lunchBreak[0]) }}
                  &dash;
                  {{ IOpeningHours.formatTime(openingHours[day].lunchBreak[1]) }}
                  </span>
                </template>
              </template>
              <template v-else>
                Geschlossen
              </template>
            </span>
          </div>
        </template>
      </div>

      <div class="d-flex justify-content-end g-mt-30">
        <div class="media g-pa-20--sm g-mb-minus-1">
          <div class="media-body">
            <div class="d-flex justify-content-between">
              <a :href="mailtoSupportEmail">Fehler melden</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
